import { useTranslations } from '@vocab/react';
import React, {
  type ChangeEvent,
  type FocusEvent,
  type FormEvent,
  forwardRef,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { FORM_VALIDATION_ERROR } from 'src/app/shared/constants/errorMessages';
import {
  PAY_RANGE_PRESSED,
  TOOLTIP_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { isPositiveNumber } from 'src/app/utils/isPositiveNumber';
import { normalizeNumber, parseNumber } from 'src/app/utils/number';
import {
  constructDefaultSalaryDisplayText,
  formatSalaryWithCurrency,
} from 'src/app/utils/salary/utils/formatSalary';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateMaxSalary } from 'src/app/v1/state/modules/formData/maxSalary';
import { actionUpdateMinSalary } from 'src/app/v1/state/modules/formData/minSalary';
import { actionUpdateSalaryText } from 'src/app/v1/state/modules/formData/salaryText';
import { actionUpdateSalaryRangeSelected } from 'src/app/v1/state/modules/tealium/salaryRangeSelected';
import { useSuggestedSalary } from 'src/app/v1/views/hooks/useSuggestedSalary';
import type { AppState } from 'src/types/appState';
import type { PayRangeRef } from 'src/types/formFields';

import translations from './.vocab';
import { PayRangeView } from './components/PayRangeView';
import { useSyncCurrencyPayType } from './hooks/useSyncCurrencyPayType';
import { appendDecimalPart } from './utils/appendDecimalPart';
import { isNumberOrNullOrDecimalSeparator } from './utils/isNumberOrNullOrDecimalSeparator';
import showSalaryMessage from './utils/showSalaryMessage';
import { makeSalaryDataValidator } from './validation';

interface IDispatchedSalaryDetails {
  maxValue?: number | null;
  minValue?: number | null;
  text?: string;
}

export const PayRange = forwardRef((_props, ref: Ref<PayRangeRef>) => {
  const { t } = useTranslations(translations);

  const { jobTitle, maxSalary, minSalary, salaryText } = useSelector(
    (state: AppState) => ({
      jobTitle: state.formData.jobTitle,
      maxSalary: state.formData.maxSalary,
      minSalary: state.formData.minSalary,
      salaryText: state.formData.salaryText,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const [salaryError, setSalaryError] = useState<string | null | undefined>(
    null,
  );
  const [salaryErrorMin, setSalaryErrorMin] = useState<
    string | null | undefined
  >(undefined);
  const [salaryErrorMax, setSalaryErrorMax] = useState<
    string | null | undefined
  >(undefined);

  const [minSalaryText, setMinSalaryText] = useState<string>('');
  const [maxSalaryText, setMaxSalaryText] = useState<string>('');
  const [minimumSalary, setMinimumSalary] = useState<number | undefined | null>(
    minSalary,
  );
  const [maximumSalary, setMaximumSalary] = useState<number | undefined | null>(
    maxSalary,
  );
  const [salaryDetails, setSalaryDetails] = useState<string>();

  const { suggestedSalary } = useSuggestedSalary();
  const { averageMin, averageMax } = suggestedSalary || {};

  const { currency, frequency, payType } = useSyncCurrencyPayType({
    onResetSalary: () => {
      setMinSalaryText('');
      setMaxSalaryText('');
      setSalaryErrorMin(undefined);
      setSalaryErrorMax(undefined);
      setSalaryError(undefined);
      setMinimumSalary(null);
      setMaximumSalary(null);
    },
  });

  const dispatchSalaryDetails = useCallback(
    ({ maxValue, minValue, text }: IDispatchedSalaryDetails) => {
      dispatch(actionUpdateMaxSalary(maxValue!));
      dispatch(actionUpdateMinSalary(minValue!));

      if (text) {
        dispatch(actionUpdateSalaryText(text));
      }

      const isSuggestedSalaryRangeSelected =
        minValue === averageMin && maxValue === averageMax;

      dispatch(actionUpdateSalaryRangeSelected(isSuggestedSalaryRangeSelected));
    },
    [dispatch, averageMin, averageMax],
  );

  // If any values of min/max, currency or pay type changes then format
  // the text box values and update salary text if its empty
  useEffect(() => {
    const minSalText = formatSalaryWithCurrency(minSalary, currency);
    const maxSalText = formatSalaryWithCurrency(maxSalary, currency);
    setMinSalaryText(isPositiveNumber(minSalary) ? minSalText : '');
    setMaxSalaryText(isPositiveNumber(maxSalary) ? maxSalText : '');
    if (!salaryText) {
      const salaryTextDetails = constructDefaultSalaryDisplayText({
        min: minSalary,
        max: maxSalary,
        currency,
        payType,
      });
      dispatch(actionUpdateSalaryText(salaryTextDetails));
    }
    // NOTE: This debliberately excludes watching changes on "salaryText"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currency, payType, maxSalary, minSalary]);

  useEffect(() => {
    if (!salaryError && !salaryErrorMin && !salaryErrorMax) {
      if (maxSalary !== maximumSalary || minSalary !== minimumSalary) {
        dispatchSalaryDetails({
          maxValue: maximumSalary,
          minValue: minimumSalary,
          text: salaryDetails,
        });
      }
    }
  }, [
    dispatchSalaryDetails,
    salaryError,
    salaryErrorMin,
    salaryErrorMax,
    salaryDetails,
    minimumSalary,
    maximumSalary,
    minSalary,
    maxSalary,
  ]);

  const handleSalaryValidation = makeSalaryDataValidator(
    t,
    currency,
    frequency,
    { setSalaryError, setSalaryErrorMax, setSalaryErrorMin },
  );
  useImperativeHandle(ref, () => ({
    handleValidation: (skipIfNull = false) =>
      handleSalaryValidation({
        maxSalary: maximumSalary,
        maxSalaryText,
        minSalary: minimumSalary,
        skipIfNull,
        validationType: undefined,
        value: undefined,
      }),
  }));

  const sendSalaryUpdateLinkEvent = (isMinSalaryUpdated: boolean) => {
    const eventData = {
      eventName: PAY_RANGE_PRESSED,
      objectInteraction: `salary-${isMinSalaryUpdated ? 'min' : 'max'}`,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  const handleSalaryMinOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    sendSalaryUpdateLinkEvent(true);

    const validationResult = handleSalaryValidation({
      minSalary: minimumSalary,
      maxSalary: maximumSalary,
      maxSalaryText,
      validationType: 'min',
      value: normalizeNumber(event.target.value),
    });

    if (validationResult) {
      const eventData = {
        eventName: FORM_VALIDATION_ERROR,
        rangeType: 'min',
        action: 'blur',
        errorMessage: validationResult,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
  };

  const handleSalaryMaxOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    sendSalaryUpdateLinkEvent(false);

    const validationResult = handleSalaryValidation({
      minSalary: minimumSalary,
      maxSalary: maximumSalary,
      maxSalaryText,
      validationType: 'max',
      value: normalizeNumber(event.target.value),
    });

    if (validationResult) {
      const eventData = {
        eventName: FORM_VALIDATION_ERROR,
        rangeType: 'max',
        action: 'blur',
        errorMessage: validationResult,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
  };

  const handleUpdateMinimumSalary = (event: ChangeEvent<HTMLInputElement>) => {
    const valid = isNumberOrNullOrDecimalSeparator(event);

    if (!valid) {
      return;
    }

    const value = event.target.value;
    const minSalaryValue = parseNumber(value);

    const minSalText = appendDecimalPart(
      value,
      formatSalaryWithCurrency(minSalaryValue, currency),
    );
    const maxSalText = formatSalaryWithCurrency(maximumSalary, currency);

    setMinSalaryText(minSalText);
    setMaxSalaryText(maxSalText);

    const salaryTextDetails = constructDefaultSalaryDisplayText({
      min: minSalaryValue,
      max: maximumSalary,
      currency,
      payType,
    });

    handleSalaryValidation({
      minSalary: minSalaryValue,
      maxSalary: maximumSalary,
      maxSalaryText,
      validationType: 'min',
      value: isNaN(minSalaryValue) ? '' : minSalaryValue.toString(),
    });

    setMinimumSalary(minSalaryValue);
    setSalaryDetails(salaryTextDetails);
  };

  const handleUpdateMaximumSalary = (event: ChangeEvent<HTMLInputElement>) => {
    const valid = isNumberOrNullOrDecimalSeparator(event);

    if (!valid) {
      return;
    }

    const value = event.target.value;
    const maxSalaryValue = parseNumber(value);

    const minSalText = formatSalaryWithCurrency(minimumSalary, currency);
    const maxSalText = appendDecimalPart(
      value,
      formatSalaryWithCurrency(maxSalaryValue, currency),
    );

    setMinSalaryText(minSalText);
    setMaxSalaryText(maxSalText);

    const salaryTextDetails = constructDefaultSalaryDisplayText({
      min: minimumSalary,
      max: maxSalaryValue,
      currency,
      payType,
    });

    handleSalaryValidation({
      minSalary: minimumSalary,
      maxSalary: maxSalaryValue,
      maxSalaryText,
      validationType: 'max',
      value: isNaN(maxSalaryValue) ? '' : maxSalaryValue.toString(),
    });

    setMaximumSalary(maxSalaryValue);
    setSalaryDetails(salaryTextDetails);
  };

  const handleToggleContextualHelp = () => {
    const eventData = {
      eventName: TOOLTIP_PRESSED,
      tooltipType: 'wage legal requirements',
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  const messageType = showSalaryMessage(
    maximumSalary,
    minimumSalary,
    suggestedSalary?.averageMax,
    suggestedSalary?.averageMin,
  );

  const tPayRangeHeader = t('pay-range-header');
  const tPayRangeDesc = t('pay-range-description');
  const tPayRangeMinPlaceholder = t('pay-range-min-placeholder');
  const tPayRangeMinLabel = t('pay-range-min-label');
  const tPayRangeMaxPlaceholder = t('pay-range-max-placeholder');
  const tPayRangeMaxLabel = t('pay-range-max-label');

  const handleMinSalaryChange = (e: FormEvent<HTMLInputElement>) =>
    handleUpdateMinimumSalary &&
    handleUpdateMinimumSalary(e as ChangeEvent<HTMLInputElement>);

  const handleMaxSalaryChange = (e: FormEvent<HTMLInputElement>) =>
    handleUpdateMaximumSalary &&
    handleUpdateMaximumSalary(e as ChangeEvent<HTMLInputElement>);

  return (
    <PayRangeView
      description={tPayRangeDesc}
      jobTitle={jobTitle}
      maxSalaryFieldLabel={tPayRangeMaxLabel}
      maxSalaryFieldMessage={salaryErrorMax}
      maxSalaryFieldPlaceholder={tPayRangeMaxPlaceholder}
      maxSalaryText={maxSalaryText}
      minSalaryFieldLabel={tPayRangeHeader}
      minSalaryFieldLabelText={tPayRangeMinLabel}
      minSalaryFieldMessage={salaryErrorMin}
      minSalaryFieldPlaceholder={tPayRangeMinPlaceholder}
      minSalaryText={minSalaryText}
      onMaxSalaryFieldBlur={handleSalaryMaxOnBlur}
      onMaxSalaryFieldChange={handleMaxSalaryChange}
      onMinSalaryFieldBlur={handleSalaryMinOnBlur}
      onMinSalaryFieldChange={handleMinSalaryChange}
      salaryError={salaryError}
      type={messageType}
      currency={currency}
      contextualHelpMessage={t('pay-range-contextual-help')}
      onToggleContextualHelp={handleToggleContextualHelp}
      suggestedSalary={suggestedSalary}
    />
  );
});
